import PropTypes from 'prop-types'
import { forwardRef, memo, useMemo, useState } from 'react'
import { normalizeSizeString } from 'app/util/sizes'
import Tooltip from 'components/interface/Tooltip/Tooltip'
import useFallbackRef from 'lib/react/hooks/useFallbackRef'
import usePressEvents from 'lib/react/hooks/usePressEvents'
import { isTouch } from 'lib/support'
import classNames from 'lib/util/classNames'
import styles from './Button.module.scss'
import ButtonBasic from './ButtonBasic'
import ButtonContextSymbol from './ButtonContextSymbol'
import ButtonSymbol from './ButtonSymbol'

const TYPES = {
  'context-symbol': ButtonContextSymbol,
  symbol: ButtonSymbol,
  basic: ButtonBasic
}

/**
 * The `Button`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
const Button = forwardRef((props, forwardedRef) => {
  const {
    Tag,
    borderRadius,
    children,
    className,
    color,
    description,
    iconAnimation,
    size,
    title,
    tooltipContent,
    tooltipMode,
    tooltipSize,
    type,
    useBorder,
    useShadow,
    useTooltip,
    wide,
    ...rest
  } = props
  const ref = useFallbackRef(forwardedRef)
  const Component = useMemo(() => TYPES[type] || ButtonBasic, [type])
  const isPressing = usePressEvents(ref, false)
  const [isHover, setIsHover] = useState(false)
  const attributes = useMemo(
    () => ({
      className: classNames([
        className,
        styles[`size-${normalizeSizeString(size)}`],
        styles[`color-${color}`],
        styles[`border-${borderRadius}`],
        useBorder && styles.useBorder,
        useShadow && styles.useShadow,
        isPressing && styles.pressing,
        iconAnimation && styles[`iconanim-${iconAnimation}`],
        wide && styles.wide
      ])
    }),
    [
      className,
      size,
      color,
      useBorder,
      useShadow,
      borderRadius,
      isPressing,
      iconAnimation,
      wide
    ]
  )

  if (useTooltip && (!isTouch || tooltipMode === 'button')) {
    return (
      <Tooltip.Target
        ref={ref}
        Tag={Component}
        onMouseEnter={() => {
          !isTouch && setIsHover(true)
        }}
        onMouseLeave={() => {
          !isTouch && setIsHover(false)
        }}
        isHover={isHover}
        {...rest}
        ElementTag={Tag}
        {...attributes}
        tabIndex="0"
        title={tooltipMode !== 'button' ? '' : title}
        tooltipMode={tooltipMode}
        tooltipContent={
          tooltipContent || (
            <Tooltip size={tooltipSize}>
              <Tooltip.Header>{title}</Tooltip.Header>
              <Tooltip.Body>{description}</Tooltip.Body>
            </Tooltip>
          )
        }
      >
        {children}
      </Tooltip.Target>
    )
  }

  return (
    <Component
      ref={ref}
      {...rest}
      ElementTag={Tag}
      {...attributes}
      tabIndex="0"
      isHover={isHover}
      title={title}
      onMouseEnter={() => {
        !isTouch && setIsHover(true)
      }}
      onMouseLeave={() => {
        !isTouch && setIsHover(false)
      }}
    >
      {children}
    </Component>
  )
})

// Display name
Button.displayName = 'Button'

/** @type {object} */
Button.propTypes = {
  borderRadius: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  color: PropTypes.string,
  size: PropTypes.string,
  Tag: PropTypes.elementType,
  title: PropTypes.string,
  description: PropTypes.string,
  tooltipContent: PropTypes.element,
  tooltipSize: PropTypes.string,
  tooltipMode: PropTypes.string,
  type: PropTypes.string,
  useBorder: PropTypes.bool,
  useShadow: PropTypes.bool,
  useTooltip: PropTypes.bool,
  useHighlight: PropTypes.bool,
  useHighlightHover: PropTypes.bool,
  iconAnimation: PropTypes.string,
  wide: PropTypes.bool
}

/** @type {object} */
Button.defaultProps = {
  borderRadius: 'a',
  children: '',
  className: '',
  color: 'a',
  size: 'standard',
  Tag: 'button',
  title: '',
  description: '',
  tooltipContent: null,
  tooltipSize: 'small',
  tooltipMode: 'standard',
  type: 'basic',
  useBorder: true,
  useShadow: false,
  useTooltip: false,
  useHighlight: false,
  useHighlightHover: false,
  iconAnimation: '',
  wide: false
}

// Memoize
export default memo(Button)
